/*
 *    Copyright [2007-2025] [wisemapping]
 *
 *   Licensed under WiseMapping Public License, Version 1.0 (the "License").
 *   It is basically the Apache License, Version 2.0 (the "License") plus the
 *   "powered by wisemapping" text requirement on every single page;
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the license at
 *
 *       https://github.com/wisemapping/wisemapping-open-source/blob/main/LICENSE.md
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import FormContainer from '../layout/form-container';
import AccountAccessLayout from '../layout/AccountAccessLayout';
import { Link as RouterLink } from 'react-router';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { trackPageView } from '../../utils/analytics';
import { SEOHead } from '../seo';
import { getCanonicalUrl, getAlternateLanguageUrls } from '../../utils/seo-locale';

const ForgotPasswordSuccessPage = (): React.ReactElement => {
  const intl = useIntl();

  useEffect(() => {
    document.title = intl.formatMessage({
      id: 'forgotsuccess.page-title',
      defaultMessage: 'Password Recovered | WiseMapping',
    });
  }, [intl]);

  // Once per visit: a language change retitles the page but is not a new page view.
  useEffect(() => {
    trackPageView(window.location.pathname, 'ForgotPassword:Success');
  }, []);

  const canonicalUrl = getCanonicalUrl('/c/forgot-password-success');
  const alternateLanguages = getAlternateLanguageUrls('/c/forgot-password-success');
  const baseUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://app.wisemapping.com';

  return (
    <>
      <SEOHead
        title={intl.formatMessage({
          id: 'forgotsuccess.page-title',
          defaultMessage: 'Password Recovered | WiseMapping',
        })}
        description={intl.formatMessage({
          id: 'seo.forgot-password-success.description',
          defaultMessage:
            'A password recovery email has been sent to your inbox. Check your email to reset your WiseMapping account password and regain access to your mind maps.',
        })}
        keywords="password recovery, reset password, forgot password, account recovery, wise mapping"
        canonicalUrl={canonicalUrl}
        alternateLanguages={alternateLanguages}
        ogType="website"
        robots="noindex, follow"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Password Recovery Email Sent - WiseMapping',
          description:
            'A password recovery email has been sent to your inbox. Check your email to reset your account password.',
          url: `${baseUrl}${canonicalUrl}`,
        }}
      />
      <AccountAccessLayout headerType="none">
        <FormContainer>
          <Typography variant="h4" component="h1">
            <FormattedMessage id="forgot.success.title" defaultMessage="Check your email." />
          </Typography>

          <Typography
            sx={{
              marginBottom: '16px',
            }}
          >
            <FormattedMessage
              id="forgot.success.desc"
              defaultMessage="We've sent a password reset link to your email. The link expires in 1 hour. Your current password remains unchanged until you use the link."
            />
          </Typography>

          <Button
            color="primary"
            size="medium"
            variant="contained"
            component={RouterLink}
            to="/c/login"
            disableElevation={true}
          >
            <FormattedMessage id="login.signin" defaultMessage="Sign In" />
          </Button>
        </FormContainer>
      </AccountAccessLayout>
    </>
  );
};

export default ForgotPasswordSuccessPage;
